import type Phaser from "phaser";
import type { SaveAdapter } from "@yjsgames/save";
import type { AnalyticsSink } from "@yjsgames/analytics-ports";
import { createControllers, type DomainControllers } from "../app/createControllers.js";
import type { GameContext } from "../app/GameContext.js";
import type { ShellSystems } from "../app/createShellSystems.js";
import type { FeatureFlags } from "../env/featureFlags.js";
import type { PieceRandomSource } from "../data/modulePieces.js";
import type { ProgressState } from "../app/saveSwap.js";
import type { ModuleBrickSaveData } from "../models/ModuleBrickSaveData.js";
import type { DailyResetService } from "@yjsgames/daily-reset";
import type { CooldownService } from "@yjsgames/cooldown";
import { setNineSliceButtonClickSound } from "@yjsgames/phaser-ui";
import { ModalHost } from "../ui/ModalHost.js";
import { TabRefresher } from "../ui/TabRefresher.js";
import { ToastPresenter } from "../ui/ToastPresenter.js";
import { createTutorialRunner } from "../tutorial/createTutorialRunner.js";
import type { TutorialRunner } from "@yjsgames/phaser-tutorial";
import { t } from "../i18n/i18n.js";
import { BattlefieldRunner } from "./BattlefieldRunner.js";
import { DungeonFlow } from "./DungeonFlow.js";
import { RebirthTransitionOverlay } from "../ui/RebirthTransitionOverlay.js";
import { createGameSceneTabRouter } from "./gameSceneTabRouter.js";
import { createGameSceneShellSystems } from "./gameSceneShellSystems.js";
import type { TabRouter } from "../app/TabRouter.js";
import type { BattleProcEvent } from "../ui/ModuleGridView.js";
import { startGameOverSequence } from "./gameOverSequence.js";
import type { DialogueTrigger } from "../data/dialogueData.js";

export interface GameSceneRuntimeDeps {
  readonly scene: Phaser.Scene;
  readonly ctx: () => GameContext;
  readonly features: FeatureFlags;
  readonly controllers: () => DomainControllers;
  readonly progress: () => ProgressState;
  readonly setControllers: (controllers: DomainControllers) => void;
  readonly setLastEnteredTab: (tab: string) => void;
  readonly lastEnteredTab: () => string | null;
  readonly setBoxResult: (message: string) => void;
  readonly pieceRng: () => PieceRandomSource;
  readonly cheatForceHouseAd: () => boolean;
  readonly toggleForceHouseAd: () => boolean;
  readonly dailyReset: () => DailyResetService;
  readonly cooldown: () => CooldownService;
  readonly readinessCanRefresh: () => boolean;
  readonly registryGet: (key: string) => unknown;
  readonly frame: () => import("../ui/ShellFrame.js").ShellFrame;
  readonly gameviewExpanded: () => boolean;
  readonly toSaveData: () => ModuleBrickSaveData;
  readonly invalidateUi: () => void;
  readonly refreshTabLocks: () => void;
  readonly forceDialogueTrigger: (trigger: DialogueTrigger) => void;
  readonly refreshTabRedDots: () => void;
  readonly refreshOnTabEnter: () => void;
  readonly refreshActiveTab: () => void;
  readonly refreshAll: () => void;
  readonly refreshHud: () => void;
  readonly persist: () => void;
  readonly persistNow: () => void;
  readonly toggleBannerPreview: () => boolean;
  readonly bannerPreviewEnabled: () => boolean;
  readonly toggleAppTimerOverlay: () => boolean;
  readonly appTimerOverlayEnabled: () => boolean;
  readonly applyTargetFps: Parameters<typeof createGameSceneShellSystems>[0]["applyTargetFps"];
  readonly pauseGameplayForAd: () => void;
  readonly resumeGameplayForAd: () => void;
  readonly battleProc: () => (procs: readonly BattleProcEvent[], startFired: boolean) => void;
  readonly onSynergyDiscovered: (id: string) => void;
}

export interface GameSceneRuntime {
  readonly controllers: DomainControllers;
  readonly modals: ModalHost;
  readonly tabs: TabRefresher;
  readonly toasts: ToastPresenter;
  readonly tabRouter: TabRouter;
  readonly shell: ShellSystems;
  readonly runner: BattlefieldRunner;
  readonly tutorialRunner: TutorialRunner;
  readonly dungeonFlow: DungeonFlow;
}

/** create()의 런타임 객체 생성을 한 composition 모듈로 모은다. */
export function createGameSceneRuntime(deps: GameSceneRuntimeDeps): GameSceneRuntime {
  const controllers = createControllers({
    saveAdapter: deps.registryGet("saveAdapter") as SaveAdapter | undefined,
    settingsAdapter: deps.registryGet("settingsAdapter") as SaveAdapter | undefined,
    analyticsSink: deps.registryGet("analyticsSink") as AnalyticsSink | undefined,
  });
  deps.setControllers(controllers);
  setNineSliceButtonClickSound(() => deps.controllers().soundController.playUi("ui.button"));

  let shell: ShellSystems | undefined;
  let runner: BattlefieldRunner | undefined;
  let tutorialRunner: TutorialRunner | undefined;
  let dungeonFlow!: DungeonFlow;
  const gameOverOverlay = new RebirthTransitionOverlay(deps.scene);
  deps.scene.events.once("shutdown", () => gameOverOverlay.destroy());
  const playChapterDefeatSequence = (): void => {
    if (gameOverOverlay.isPlaying) return;
    runner?.setPaused(true);
    startGameOverSequence({
      isOverlayPlaying: () => gameOverOverlay.isPlaying,
      playPlayerShipDestruction: () => deps.frame().playPlayerShipDestruction(),
      scheduleDelay: (delayMs, callback) => {
        deps.scene.time.delayedCall(delayMs, callback);
      },
      playOverlay: () => {
        gameOverOverlay.play(
          () => {
            shell!.chapter.loseBattleToChapterOne();
            toasts.showCenterToast(t("battle.defeat.title"));
          },
          {
            texts: [t("battle.defeat.title"), t("battle.defeat.returning")],
            onDone: () => {
              deps.frame().restorePlayerShip();
              runner?.setPaused(false);
            },
          },
        );
      },
    });
  };
  const previewRebirthTransition = (): void => {
    if (gameOverOverlay.isPlaying) return;
    runner?.setPaused(true);
    gameOverOverlay.play(
      () => {
        deps.controllers().soundController.play("rebirth");
      },
      {
        texts: [t("rebirth.transition.doing"), t("rebirth.transition.returning")],
        onDone: () => runner?.setPaused(false),
      },
    );
  };

  const modals = new ModalHost(
    deps.scene,
    {
      onOpen: () => {
        deps.controllers().soundController.playUi("ui.modalOpen");
        shell?.crazyGamesBannerController?.evaluate();
      },
      onClose: () => {
        deps.controllers().soundController.playUi("ui.modalClose");
        shell?.crazyGamesBannerController?.evaluate();
      },
    },
    deps.invalidateUi,
  );
  const tabs = new TabRefresher();
  const toasts = new ToastPresenter(deps.scene);
  const tabRouter = createGameSceneTabRouter({
    features: deps.features,
    modals,
    tabs,
    frame: deps.frame,
    runner: () => runner,
    controllers: deps.controllers,
    progress: deps.progress,
    lastEnteredTab: deps.lastEnteredTab,
    setLastEnteredTab: deps.setLastEnteredTab,
    tutorialRunner: () => tutorialRunner,
    guideQuest: () => shell?.guideQuest,
    gameviewExpanded: deps.gameviewExpanded,
    toasts,
    refreshTabRedDots: deps.refreshTabRedDots,
    persist: deps.persist,
    refreshOnTabEnter: deps.refreshOnTabEnter,
    evaluateCrazyGamesBanner: () => shell?.crazyGamesBannerController?.evaluate(),
  });

  shell = createGameSceneShellSystems({
    scene: deps.scene,
    ctx: deps.ctx(),
    features: deps.features,
    modals,
    controllers: deps.controllers,
    replaceControllers: deps.setControllers,
    progress: deps.progress,
    setBoxResult: deps.setBoxResult,
    pieceRng: deps.pieceRng,
    cheatForceHouseAd: deps.cheatForceHouseAd,
    toggleForceHouseAd: deps.toggleForceHouseAd,
    dailyReset: deps.dailyReset,
    cooldown: deps.cooldown,
    readinessCanRefresh: deps.readinessCanRefresh,
    tabs,
    frame: deps.frame,
    runner: () => runner!,
    applySpaceshipVisualScale: () => runner?.applySpaceshipVisualScale(),
    tutorialRunner: () => tutorialRunner,
    tabRouter: () => tabRouter,
    toasts,
    registryGet: deps.registryGet,
    toSaveData: deps.toSaveData,
    invalidateUi: deps.invalidateUi,
    refreshTabLocks: deps.refreshTabLocks,
    forceDialogueTrigger: deps.forceDialogueTrigger,
    refreshTabRedDots: deps.refreshTabRedDots,
    refreshActiveTab: deps.refreshActiveTab,
    refreshAll: deps.refreshAll,
    refreshHud: deps.refreshHud,
    persist: deps.persist,
    persistNow: deps.persistNow,
    toggleBannerPreview: deps.toggleBannerPreview,
    bannerPreviewEnabled: deps.bannerPreviewEnabled,
    triggerChapterDefeat: playChapterDefeatSequence,
    previewRebirthTransition,
    toggleAppTimerOverlay: deps.toggleAppTimerOverlay,
    appTimerOverlayEnabled: deps.appTimerOverlayEnabled,
    applyTargetFps: deps.applyTargetFps,
    pauseGameplayForAd: deps.pauseGameplayForAd,
    resumeGameplayForAd: deps.resumeGameplayForAd,
  });

  runner = new BattlefieldRunner({
    scene: deps.scene,
    ctx: deps.ctx(),
    combat: shell.combat,
    gameviewMask: () => deps.frame()?.gameviewMask,
    onProc: (ids, startFired) => deps.battleProc()(ids, startFired),
    onFieldCleared: () =>
      dungeonFlow?.isActive() ? dungeonFlow.onFieldCleared() : shell!.chapter.nextChapter(),
    onGameOver: playChapterDefeatSequence,
    defeatDeadlineY: () => deps.frame().defeatDeadlineY,
    isDefeatPressureEnabled: () => !(dungeonFlow?.isActive() ?? false),
    persistNow: deps.persistNow,
    rewardTargetPos: (kind) => deps.frame().getRewardTargetPos(kind),
    goldGainMultiplier: () => shell!.cheats.goldGainMultiplier,
    onSynergyDiscovered: deps.onSynergyDiscovered,
  });
  runner.attach();

  tutorialRunner = createTutorialRunner(deps.scene, {
    getCompleted: () => deps.progress().tutorialCompleted,
    getResume: () => deps.progress().tutorialResume,
    persist: deps.persistNow,
    translate: t,
    analytics: deps.controllers().analytics,
  });

  dungeonFlow = new DungeonFlow({
    scene: deps.scene,
    ctx: deps.ctx(),
    ports: shell.ports,
    battlefield: {
      clearField: () => runner!.clearField(),
      buildDungeonGrid: (i) => runner!.buildDungeonGrid(i),
      buildChapterGrid: () => runner!.buildChapterGrid(),
    },
    timerBar: () => deps.frame().dungeonTimerBar,
    setActiveTab: (tab) => tabRouter.setActive(tab),
    showCenterToast: (m) => toasts.showCenterToast(m),
    grantDungeonReward: (i) => shell!.chapter.grantDungeonReward(i),
    onActionCompleted: (target) => shell!.guideQuest.notifyActionCompleted(target),
  });

  return { controllers, modals, tabs, toasts, tabRouter, shell, runner, tutorialRunner, dungeonFlow };
}
